import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { PracticeFlashcard } from "../components/PracticeFlashcard.tsx";
import { useAppServices } from "../hooks/AppContext.tsx";
import { usePractice, useVocabularySnapshot } from "../hooks/bindings.ts";
import { findMeaningById, findWordById } from "../../domain/vocabulary/grouping.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function PracticeSessionScreen() {
  const { practice } = useAppServices();
  const state = usePractice();
  const snapshot = useVocabularySnapshot();
  const navigate = useNavigate();
  const [answer, setAnswer] = useState("");
  const [optionId, setOptionId] = useState<string>();
  const session = state.cardSession ? undefined : state.session;
  const progress = practice.progress();
  const phase = session?.phase;
  const blocked = phase === "saving" || phase === "save_failed";

  useEffect(() => {
    if (session?.ended && !session.unsavedExit) {
      navigate("/practice/summary", { replace: true });
    }
  }, [session, navigate]);

  useEffect(() => {
    if (!session || session.ended) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [session]);

  if (state.cardSession) {
    return <PracticeFlashcard />;
  }

  if (!session?.current) {
    return (
      <Frame>
        <Screen withNav={false}>
          <TopBar title="Practice" backTo="/practice" />
          <div className={styles.empty}>
            {session
              ? "This session has no current question. Go back and start again."
              : "Preparing your practice session…"}
          </div>
        </Screen>
      </Frame>
    );
  }

  const meaning = findMeaningById(snapshot, session.current.meaningId);
  const word = meaning ? findWordById(snapshot, meaning.wordId) : undefined;
  const exercise = session.current.exercise;
  const showFeedback = phase === "feedback" || phase === "save_failed" || phase === "saving";

  async function check(unknown = false) {
    if (blocked || phase !== "question") {
      return;
    }
    await practice.submit({ value: answer, optionId, unknown });
    setAnswer("");
    setOptionId(undefined);
  }

  const feedbackLabel =
    session.lastOutcome === "correct"
      ? "Correct!"
      : session.lastOutcome === "unknown"
        ? "Here’s the answer"
        : session.lastOutcome === "incorrect"
          ? "Incorrect"
          : "Saved";

  const roleLabel = session.current.role === "assessment" ? "Standard assessment" : "Supporting practice";

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar
          title="Practice"
          onBack={() => {
            const unsaved = phase === "saving" || phase === "save_failed";
            const message = unsaved
              ? "This result has not been saved. Leave anyway?"
              : "Leave this practice session? Progress already saved will remain, but the rest of the session is in memory.";
            if (window.confirm(message)) {
              practice.end(true);
              navigate("/practice/summary");
            }
          }}
          right={
            progress ? (
              <span>
                {progress.completed} / {progress.total}
                {progress.retries ? ` · ${progress.retries} extra` : ""}
              </span>
            ) : null
          }
        />
        {phase === "conflict" ? (
          <div className={styles.banner}>
            <div>
              {session.conflictMessage ?? "Vocabulary changed in another window. Restart practice with the current words."}
              <div className={styles.stack} style={{ marginTop: 10 }}>
                <PrimaryButton onClick={() => navigate("/practice")}>Restart</PrimaryButton>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "clock_blocked" ? (
          <div className={styles.banner}>
            <div>
              {session.clockMessage}
              <div className={styles.stack} style={{ marginTop: 10 }}>
                <PrimaryButton onClick={() => navigate("/practice")}>Back to practice</PrimaryButton>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "save_failed" ? (
          <p className={styles.error}>
            {session.persistError} This result has not been saved.
            <span className={styles.stack} style={{ marginTop: 10, display: "block" }}>
              <PrimaryButton onClick={() => void practice.retrySave()}>Retry save</PrimaryButton>
            </span>
          </p>
        ) : null}
        {session.unsavedExit ? (
          <p className={styles.error}>The last result was not saved.</p>
        ) : null}
        <div className={styles.progressTrack}>
          <div
            className={styles.progressValue}
            style={{ width: `${progress ? (progress.completed / Math.max(progress.total, 1)) * 100 : 0}%` }}
          />
        </div>
        {meaning ? <TypeTag type={meaning.type} /> : null}
        <p className={styles.practiceStatus}>{roleLabel}</p>
        {showFeedback && session.lastOutcome ? (
          <div className={styles.feedback}>
            <h2 className={session.lastOutcome === "correct" ? styles.ok : styles.bad}>{feedbackLabel}</h2>
            <p className={styles.question}>{exercise.question}</p>
            <p>
              Correct answer: <strong>{exercise.correctAnswer}</strong>
            </p>
            <p className={styles.sub}>
              {word?.displayText}
              {meaning ? ` · ${meaning.shortLabel}` : ""}
            </p>
            {phase === "saving" ? <p className={styles.sub}>Saving…</p> : null}
            <div className={styles.stack}>
              <PrimaryButton disabled={blocked} onClick={() => practice.next()}>
                Next
              </PrimaryButton>
              <PrimaryButton secondary disabled={blocked} onClick={() => practice.practiseAgain()}>
                Practise this again
              </PrimaryButton>
              <button
                className={styles.textButton}
                type="button"
                disabled={blocked}
                onClick={() => void practice.reportQuestionProblem()}
              >
                Question problem / valid alternative
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 23, marginTop: 24 }}>
              {exercise.kind === "meaning_to_word"
                ? "What word means…"
                : exercise.kind === "confusable_context"
                  ? "Choose the best word"
                  : "Complete the sentence"}
            </h1>
            <p className={styles.question}>{exercise.question}</p>
            {exercise.kind === "confusable_context" ? (
              <div>
                {exercise.options.map((option) => (
                  <label
                    key={option.id}
                    className={`${styles.choice} ${optionId === option.id ? styles.choiceSelected : ""}`}
                  >
                    <input
                      type="radio"
                      name="option"
                      checked={optionId === option.id}
                      onChange={() => setOptionId(option.id)}
                      disabled={blocked}
                    />
                    {option.label}
                  </label>
                ))}
                <div className={styles.stack}>
                  <PrimaryButton disabled={!optionId || blocked} onClick={() => void check()}>
                    Check answer
                  </PrimaryButton>
                  <PrimaryButton secondary disabled={blocked} onClick={() => void check(true)}>
                    I don’t know
                  </PrimaryButton>
                  <button
                    className={styles.textButton}
                    type="button"
                    disabled={blocked}
                    onClick={() => void practice.reportQuestionProblem()}
                  >
                    Question problem / valid alternative
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void check();
                }}
              >
                <label className={styles.field}>
                  Your answer
                  <input
                    className={styles.input}
                    value={answer}
                    onChange={(event) => setAnswer(event.target.value)}
                    autoComplete="off"
                    placeholder="Type your answer…"
                    disabled={blocked}
                  />
                </label>
                <div className={styles.stack}>
                  <PrimaryButton type="submit" disabled={blocked}>
                    Check
                  </PrimaryButton>
                  <PrimaryButton secondary type="button" disabled={blocked} onClick={() => void check(true)}>
                    I don’t know
                  </PrimaryButton>
                  <button
                    className={styles.textButton}
                    type="button"
                    disabled={blocked}
                    onClick={() => void practice.reportQuestionProblem()}
                  >
                    Question problem / valid alternative
                  </button>
                </div>
              </form>
            )}
          </>
        )}
        <button
          className={styles.textButton}
          type="button"
          onClick={() => {
            practice.end(true);
            navigate("/practice/summary");
          }}
        >
          End session
        </button>
      </Screen>
    </Frame>
  );
}
